import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Mail, Users, CheckCircle2, AlertCircle, ArrowDown, ArrowUp } from 'lucide-react';
import apiClient from '../utils/axios';

export default function NurtureAnalytics() {
  const [overview, setOverview] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [sequenceAnalytics, setSequenceAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOverview();
    fetchSequences();
  }, []);

  useEffect(() => {
    if (selectedSequence) {
      fetchSequenceAnalytics(selectedSequence);
    }
  }, [selectedSequence]);

  const fetchOverview = async () => {
    try {
      const response = await apiClient.get('/nurture-analytics/overview');
      setOverview(response.data.overview);
    } catch (error) {
      console.error('Error fetching overview:', error);
    }
  };

  const fetchSequences = async () => {
    try {
      const response = await apiClient.get('/nurture-sequences');
      setSequences(response.data.sequences || []);
    } catch (error) {
      console.error('Error fetching sequences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSequenceAnalytics = async (sequenceId) => {
    try {
      const response = await apiClient.get(`/nurture-analytics/sequences/${sequenceId}`);
      setSequenceAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching sequence analytics:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <BarChart3 size={32} className="text-purple-600" />
          Nurture Analytics
        </h1>
        <p className="mt-2 text-gray-600">Performance metrics and insights for nurture sequences</p>
      </div>

      {/* Overview Cards */}
      {overview && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Sequences</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{overview.total_sequences}</p>
                <p className="text-xs text-gray-500 mt-1">{overview.active_sequences} active</p>
              </div>
              <Mail size={32} className="text-purple-600 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Enrollments</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{overview.total_enrollments}</p>
                <p className="text-xs text-gray-500 mt-1">{overview.active_enrollments} active</p>
              </div>
              <Users size={32} className="text-blue-600 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Overall Reply Rate</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{overview.overall_reply_rate}%</p>
                <p className="text-xs text-gray-500 mt-1">{overview.total_replies} replies</p>
              </div>
              <CheckCircle2 size={32} className="text-green-600 opacity-50" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Open Rate</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{overview.overall_open_rate}%</p>
                <p className="text-xs text-gray-500 mt-1">{overview.total_opens} opens</p>
              </div>
              <TrendingUp size={32} className="text-yellow-600 opacity-50" />
            </div>
          </div>
        </div>
      )}

      {/* Sequence Selector */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Sequence:</label>
        <select
          value={selectedSequence || ''}
          onChange={(e) => setSelectedSequence(e.target.value)}
          className="w-full md:w-1/3 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="">-- Select a sequence --</option>
          {sequences.map((seq) => (
            <option key={seq.id} value={seq.id}>
              {seq.name} ({seq.status})
            </option>
          ))}
        </select>
      </div>

      {/* Sequence Analytics */}
      {sequenceAnalytics && (
        <div className="space-y-6">
          {/* Overall Metrics */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Sequence Performance</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total Enrollments</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{sequenceAnalytics.overall_metrics.total_enrollments}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Open Rate</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{sequenceAnalytics.overall_metrics.open_rate}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Click Rate</p>
                <p className="text-2xl font-bold text-yellow-600 mt-1">{sequenceAnalytics.overall_metrics.click_rate}%</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Reply Rate</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{sequenceAnalytics.overall_metrics.reply_rate}%</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t">
              <p className="text-sm text-gray-600">Average Time to Reply</p>
              <p className="text-lg font-semibold text-gray-900 mt-1">
                {sequenceAnalytics.overall_metrics.avg_days_to_reply.toFixed(1)} days
              </p>
            </div>
          </div>

          {/* Step Metrics */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Step Performance</h2>
            <div className="space-y-4">
              {sequenceAnalytics.step_metrics.map((step) => (
                <div key={step.step_order} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        Step {step.step_order}: {step.step_name}
                      </h3>
                      <p className="text-sm text-gray-600 mt-1">{step.subject_line}</p>
                    </div>
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                      {step.total_sends} sends
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-600">Open Rate</p>
                      <p className="text-lg font-semibold text-blue-600 mt-1">{step.open_rate}%</p>
                      <p className="text-xs text-gray-500">{step.opens} opens</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Click Rate</p>
                      <p className="text-lg font-semibold text-yellow-600 mt-1">{step.click_rate}%</p>
                      <p className="text-xs text-gray-500">{step.clicks} clicks</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Reply Rate</p>
                      <p className="text-lg font-semibold text-green-600 mt-1">{step.reply_rate}%</p>
                      <p className="text-xs text-gray-500">{step.replies} replies</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Drop-off Analysis */}
          {sequenceAnalytics.drop_off_analysis && sequenceAnalytics.drop_off_analysis.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Drop-off Analysis</h2>
              <div className="space-y-3">
                {sequenceAnalytics.drop_off_analysis.map((step) => (
                  <div key={step.step_order} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Step {step.step_order}: {step.step_name}</p>
                      <p className="text-sm text-gray-600 mt-1">
                        {step.reached} reached • {step.dropped_off} dropped off
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`text-lg font-semibold ${step.drop_off_rate > 30 ? 'text-red-600' : step.drop_off_rate > 15 ? 'text-yellow-600' : 'text-green-600'}`}>
                        {step.drop_off_rate}%
                      </p>
                      <p className="text-xs text-gray-500">drop-off</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!selectedSequence && (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
          <BarChart3 size={48} className="mx-auto mb-4 text-gray-400" />
          <p>Select a sequence above to view detailed analytics</p>
        </div>
      )}
    </div>
  );
}


